import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SegmentedFilter } from "@/components/ui/segmented-filter";
import { cn } from "@/lib/utils";
import {
  Plus,
  PencilSimple,
  Copy,
  Trash,
  Clock,
  Monitor,
  HouseSimple,
  Globe,
} from "@phosphor-icons/react";

import type { TherapistServiceItem } from "@/types";

type Service = TherapistServiceItem;
type ServiceFilter = "all" | "active" | "inactive";

interface ServicesListProps {
  services: Service[];
  editingId: string | null;
  onEdit: (service: Service) => void;
  onDuplicate: (service: Service) => void;
  onDelete: (id: string) => void;
  onToggleActive: (service: Service) => void;
  onCreateNew: () => void;
  isDeleting: boolean;
}

const MODALITY_CHIP = {
  online: { icon: Monitor, labelKey: "therapist.services.modalityOnline", className: "bg-primary/10 text-primary" },
  in_person: { icon: HouseSimple, labelKey: "therapist.services.modalityInPerson", className: "bg-amber-100/80 text-amber-800" },
  both: { icon: Globe, labelKey: "therapist.services.modalityBoth", className: "bg-primary/10 text-primary" },
} as const;

export function ServicesList({
  services,
  editingId,
  onEdit,
  onDuplicate,
  onDelete,
  onToggleActive,
  onCreateNew,
  isDeleting,
}: ServicesListProps) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ServiceFilter>("all");

  if (services.length === 0) {
    return (
      <Card className="border-[#ece8df]">
        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
          <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-primary/10">
            <Plus className="size-8 text-primary" />
          </div>
          <h3
            className="text-lg font-semibold"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {t("therapist.services.emptyTitle")}
          </h3>
          <p className="mt-2 max-w-sm text-sm text-muted-foreground">
            {t("therapist.services.emptyText")}
          </p>
          <Button className="mt-6" onClick={onCreateNew}>
            <Plus className="size-4" />
            {t("therapist.services.createFirst")}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const visible = services
    .filter((s) => s.id !== editingId)
    .filter((s) => {
      if (filter === "active") return !!s.isActive;
      if (filter === "inactive") return !s.isActive;
      return true;
    });

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex sm:justify-end">
        <SegmentedFilter
          ariaLabel={t("therapist.services.title")}
          options={(["all", "active", "inactive"] as const).map((f) => ({
            value: f,
            label: t(`therapist.services.filter.${f}`),
          }))}
          value={filter}
          onChange={setFilter}
        />
      </div>

      {/* Cards grid */}
      <div className="grid gap-4 sm:grid-cols-2">
        {visible.map((service) => {
          const chip = MODALITY_CHIP[service.modality ?? "online"];
          const ChipIcon = chip.icon;
          const isActive = !!service.isActive;
          return (
            <article
              key={service.id}
              className={cn(
                "flex flex-col gap-2.5 rounded-2xl border border-border/80 bg-card p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md",
                !isActive && "opacity-60 hover:opacity-90"
              )}
            >
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold",
                    chip.className
                  )}
                >
                  <ChipIcon className="size-3" />
                  {t(chip.labelKey)}
                </span>
                {!isActive && (
                  <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                    {t("therapist.services.inactiveBadge")}
                  </span>
                )}
                <button
                  role="switch"
                  aria-checked={isActive}
                  title={isActive ? t("therapist.services.deactivate") : t("therapist.services.activate")}
                  onClick={() => onToggleActive(service)}
                  className={cn(
                    "relative ml-auto h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors duration-200",
                    isActive ? "bg-primary" : "bg-border"
                  )}
                >
                  <span
                    className={cn(
                      "absolute left-0.5 top-0.5 size-4 rounded-full bg-white shadow transition-transform duration-200",
                      isActive && "translate-x-4"
                    )}
                  />
                </button>
              </div>

              <h3
                className="text-base font-semibold leading-snug tracking-tight"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {service.name}
              </h3>
              <p className="line-clamp-2 min-h-10 text-sm leading-relaxed text-muted-foreground">
                {service.description}
              </p>

              <div className="mt-auto flex items-center gap-4 border-t border-dashed border-border pt-3">
                <span
                  className="text-base font-bold tabular-nums"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  {service.basePrice ?? service.price} <span className="text-xs font-semibold text-muted-foreground">KM</span>
                </span>
                <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Clock className="size-3.5" />
                  {service.durationMinutes} min
                </span>
                <div className="ml-auto flex items-center gap-0.5">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    title={t("therapist.services.edit")}
                    onClick={() => onEdit(service)}
                  >
                    <PencilSimple className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    title={t("therapist.services.duplicate")}
                    onClick={() => onDuplicate(service)}
                  >
                    <Copy className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    title={t("therapist.services.delete")}
                    onClick={() => {
                      if (confirm(t("therapist.services.confirmDelete"))) {
                        onDelete(service.id);
                      }
                    }}
                    disabled={isDeleting}
                  >
                    <Trash className="size-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </article>
          );
        })}

        {/* Ghost card: add new service */}
        <button
          type="button"
          onClick={onCreateNew}
          className="group flex min-h-44 flex-col items-center justify-center gap-3 rounded-2xl border-[1.5px] border-dashed border-border bg-card/50 text-muted-foreground transition-all duration-200 hover:border-primary/50 hover:bg-card hover:text-primary"
        >
          <span className="flex size-11 items-center justify-center rounded-full bg-primary/10 text-primary transition-transform duration-300 group-hover:rotate-90 group-hover:scale-110">
            <Plus className="size-5" weight="bold" />
          </span>
          <span className="text-sm font-semibold">{t("therapist.services.addNew")}</span>
        </button>
      </div>
    </div>
  );
}
